import { useState } from "react";
import { ListGroup, Button, Form, Spinner, Dropdown } from "react-bootstrap";
import Message from "./Message";
import Loader from "./Loader";
import { toast } from "react-toastify";

import { useGetDeliveryPartnersQuery } from "../slices/usersApiSlice";
import {
  useAssignDeliveryPartnerMutation,
  useUpdateOrderDeliveryMutation,
} from "../slices/ordersApiSlice";

const AssignDeliveryPartner = ({ order, orderId, refetch, userInfo }) => {
  const {
    data: deliveryPartners = [],
    isLoading: isLoadingDeliveryPartners,
    error: errorDeliveryPartners,
  } = useGetDeliveryPartnersQuery("approved");

  const [assignDeliveryPartner, { isLoading: isLoadingAssignDeliveryPartner }] =
    useAssignDeliveryPartnerMutation();

  const [updateOrderDelivery, { isLoading: isLoadingOrderDelivery }] =
    useUpdateOrderDeliveryMutation();

  const [selectedDeliveryPartner, setSelectedDeliveryPartner] = useState(
    order?.delivery?.deliveryPartner?._id || null
  );

  const handleAssignDeliveryPartner = async () => {
    try {
      if (!selectedDeliveryPartner) {
        toast.error("Please select a delivery partner", { autoClose: 2000 });
        return;
      }

      await assignDeliveryPartner({
        orderId,
        deliveryPartnerUserId: selectedDeliveryPartner,
      }).unwrap();

      await refetch();
      setSelectedDeliveryPartner(null);
      toast.success("Delivery partner assigned", { autoClose: 1000 });
    } catch (err) {
      toast.error(err?.data?.message || err.error);
    }
  };

  const updateOrderDeliveryHandler = async () => {
    try {
      await updateOrderDelivery(orderId);
      refetch();
      toast.success("Order marked as delivered", { autoClose: 1500 });
    } catch (error) {
      toast.error(error?.data?.message || error.message);
    }
  };

  return (
    <div className="list-group border rounded p-1 mt-3">
      <ListGroup variant="flush">
        <ListGroup.Item>
          <h2>Delivery Partner</h2>

          {order?.delivery?.deliveryPartner ? (
            <Message variant="info">
              Assigned to:{" "}
              <strong>
                {order.delivery.deliveryPartner.name} (
                {order.delivery.deliveryPartner.phone})
              </strong>
            </Message>
          ) : (
            <Message variant="warning">No delivery partner assigned</Message>
          )}

          {(!order?.delivery?.isDelivered && !order?.sourcing?.isCollected) && (
            <>
              <hr />
              <h5>Assign Delivery Partner</h5>

              {isLoadingDeliveryPartners ? (
                <Loader />
              ) : errorDeliveryPartners ? (
                <Message variant="danger">
                  {errorDeliveryPartners?.data?.message ||
                    errorDeliveryPartners.error}
                </Message>
              ) : (
                <>
                  <Dropdown className="my-2">
                    <Dropdown.Toggle
                      variant="light"
                      className="w-100 d-flex justify-content-between align-items-center border"
                    >
                      {selectedDeliveryPartner
                        ? `Selected: ${
                            deliveryPartners.find(
                              (p) => p._id === selectedDeliveryPartner
                            )?.name
                          }`
                        : "Select Delivery Partner"}
                    </Dropdown.Toggle>

                    <Dropdown.Menu className="w-100">
                      {deliveryPartners.map((partner) => (
                        <Dropdown.Item as="div" key={partner._id}>
                          <Form.Check
                            type="radio"
                            name="deliveryPartner"
                            id={partner._id}
                            label={`${partner.name} (${partner.phone})`}
                            checked={selectedDeliveryPartner === partner._id}
                            onChange={() =>
                              setSelectedDeliveryPartner(partner._id)
                            }
                          />
                        </Dropdown.Item>
                      ))}
                    </Dropdown.Menu>
                  </Dropdown>

                  <Button
                    className="mt-2"
                    onClick={handleAssignDeliveryPartner}
                    disabled={
                      isLoadingAssignDeliveryPartner || !selectedDeliveryPartner
                    }
                  >
                    {isLoadingAssignDeliveryPartner ? (
                      <>
                        <Spinner animation="border" size="sm" /> Assigning...
                      </>
                    ) : (
                      "Assign Delivery Partner"
                    )}
                  </Button>
                </>
              )}
            </>
          )}
        </ListGroup.Item>

        <ListGroup.Item>
          {userInfo?.isAdmin &&
            order.isPaid &&
            !order.delivery.isDelivered && (
              <>
                <h5>Order Delivery</h5>
                <Button onClick={updateOrderDeliveryHandler}>
                  Mark order as delivered
                </Button>
              </>
            )}
          {isLoadingOrderDelivery && <Loader />}
        </ListGroup.Item>
      </ListGroup>
    </div>
  );
};

export default AssignDeliveryPartner;